import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { PackageX, ShieldOff, type LucideIcon } from 'lucide-react';
import { AppSidebar } from './AppSidebar';
import { AppTopBar } from './AppTopBar';
import { useCapabilities } from '../hooks/useCapabilities';
import { useModuleAccess } from '../hooks/useModuleAccess';

function AccessNoticePage({
  icon: Icon,
  title,
  text,
  hint,
}: {
  icon: LucideIcon;
  title: string;
  text: string;
  hint: string;
}) {
  const { instanceId } = useParams<{ instanceId: string }>();
  const dashboardPath = `/i/${encodeURIComponent(instanceId ?? 'default')}/dashboard`;

  return (
    <div className="h-screen min-h-0 flex overflow-hidden bg-app-bg font-sans">
      <AppSidebar />
      <main className="flex-1 min-h-0 flex flex-col min-w-0 overflow-hidden pb-16 md:pb-0">
        <AppTopBar />
        <div className="flex-1 min-h-0 flex items-center justify-center px-4 py-10">
          <div className="w-full max-w-md rounded-2xl border border-app-border bg-app-surface px-6 py-8 text-center shadow-sm">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-app-surface-muted text-app-text-subtle">
              <Icon className="h-6 w-6" />
            </div>
            <h1 className="text-lg font-bold text-app-text">{title}</h1>
            <p className="mt-2 text-sm leading-relaxed text-app-text-muted">{text}</p>
            <p className="mt-1 text-sm text-app-text-subtle">{hint}</p>
            <Link
              to={dashboardPath}
              className="mt-6 inline-flex items-center justify-center rounded-lg bg-pipedrive-blue px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Zur Übersicht
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}

export function AccessDeniedPage({ areaLabel }: { areaLabel?: string }) {
  return (
    <AccessNoticePage
      icon={ShieldOff}
      title="Kein Zugriff"
      text={areaLabel ? `Du hast auf den Bereich „${areaLabel}“ keinen Zugriff.` : 'Du hast auf diesen Bereich keinen Zugriff.'}
      hint="Wenn du denkst, dass das ein Fehler ist, melde dich bei einem Admin."
    />
  );
}

/** Das Modul ist für die Instanz abgewählt (Admin Center), unabhängig von den Rechten des Nutzers. */
export function ModuleDisabledPage({ areaLabel }: { areaLabel?: string }) {
  return (
    <AccessNoticePage
      icon={PackageX}
      title="Nicht freigeschaltet"
      text={
        areaLabel
          ? `Der Bereich „${areaLabel}“ ist für euren Betrieb nicht freigeschaltet.`
          : 'Dieser Bereich ist für euren Betrieb nicht freigeschaltet.'
      }
      hint="Wende dich an Meisterpfad, wenn ihr ihn nutzen möchtet."
    />
  );
}

export function CapabilityGate({
  capability,
  areaLabel,
  children,
}: {
  capability: string;
  areaLabel?: string;
  children: React.ReactNode;
}) {
  const { loaded, can } = useCapabilities();
  const { loaded: modulesLoaded, isCapabilityAvailable } = useModuleAccess();

  if (!loaded || !modulesLoaded) {
    return (
      <div className="h-screen min-h-0 flex overflow-hidden bg-app-bg font-sans">
        <AppSidebar />
        <main className="flex-1 min-h-0" />
      </div>
    );
  }

  if (!can(capability)) {
    // Erst das Modul: ohne Buchung hilft auch kein Admin, der Rechte vergibt.
    if (!isCapabilityAvailable(capability)) return <ModuleDisabledPage areaLabel={areaLabel} />;
    return <AccessDeniedPage areaLabel={areaLabel} />;
  }

  return <>{children}</>;
}
